"use client";

import Link from "next/link";
import { useEffect, useRef, useState, useTransition } from "react";
import type { ReactNode } from "react";
import { UserPlus, UserRoundCheck, X } from "lucide-react";
import type { FriendMutationResult, FriendRelationshipStatus } from "@/lib/friend-settings";
import { isDeletedUser, publicUserName } from "@/lib/user-display";
import { FavoriteTeamMark } from "@/components/site/favorite-team-mark";
import { UserAvatar, type AvatarUser } from "@/components/site/user-avatar";

type PopoverUser = AvatarUser & {
  id: string;
  role?: string | null;
  forumModerator?: boolean;
  forumTrusted?: boolean;
  favoriteTeam?: { name: string; slug?: string; logoUrl: string | null; externalTeam?: { crest: string | null } | null } | null;
};

type ProfilePayload = {
  relationshipStatus: FriendRelationshipStatus;
  friendshipId: string | null;
  canViewFullProfile?: boolean;
};

type MutationPayload = FriendMutationResult & {
  friends?: unknown;
};

async function readJson<T>(response: Response) {
  if (response.status === 401) throw new Error("auth");
  if (!response.ok) throw new Error("request");
  return (await response.json()) as T;
}

export function UserProfilePopover({
  user,
  currentUserId,
  children,
}: {
  user: PopoverUser;
  currentUserId: string | null;
  children?: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [relationshipStatus, setRelationshipStatus] = useState<FriendRelationshipStatus>("NONE");
  const [friendshipId, setFriendshipId] = useState<string | null>(null);
  const [canViewFullProfileOverride, setCanViewFullProfileOverride] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [pending, startTransition] = useTransition();
  const rootRef = useRef<HTMLDivElement>(null);
  const name = publicUserName(user);
  const deleted = isDeletedUser(user);
  const isSelf = currentUserId === user.id;
  const canViewFullProfile = !deleted && (isSelf || canViewFullProfileOverride || relationshipStatus === "FRIEND");

  useEffect(() => {
    if (!open) return;

    function handlePointerDown(event: PointerEvent) {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (rootRef.current?.contains(target)) return;
      setOpen(false);
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") setOpen(false);
    }

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  useEffect(() => {
    if (!open || deleted || !currentUserId || isSelf) return;

    let active = true;
    setMessage(null);
    fetch(`/api/friends/profile?userId=${encodeURIComponent(user.id)}`, { headers: { accept: "application/json" } })
      .then((response) => readJson<ProfilePayload>(response))
      .then((payload) => {
        if (!active) return;
        setRelationshipStatus(payload.relationshipStatus);
        setFriendshipId(payload.friendshipId);
        setCanViewFullProfileOverride(Boolean(payload.canViewFullProfile));
      })
      .catch((error) => {
        if (!active) return;
        setMessage(error instanceof Error && error.message === "auth" ? "Belépés szükséges" : "Nem sikerült betölteni.");
      });

    return () => {
      active = false;
    };
  }, [currentUserId, deleted, isSelf, open, user.id]);

  function runAction(path: string, body: Record<string, string>, optimistic?: FriendRelationshipStatus) {
    if (!currentUserId || isSelf) return;
    const previousStatus = relationshipStatus;
    const previousFriendshipId = friendshipId;
    if (optimistic) setRelationshipStatus(optimistic);
    setMessage(null);

    startTransition(async () => {
      try {
        const payload = await readJson<MutationPayload>(
          await fetch(path, {
            method: "POST",
            headers: { "Content-Type": "application/json", accept: "application/json" },
            body: JSON.stringify(body),
          }),
        );
        setMessage(payload.message);
        const refreshed = await readJson<ProfilePayload>(
          await fetch(`/api/friends/profile?userId=${encodeURIComponent(user.id)}`, { headers: { accept: "application/json" } }),
        );
        setRelationshipStatus(refreshed.relationshipStatus);
        setFriendshipId(refreshed.friendshipId);
        setCanViewFullProfileOverride(Boolean(refreshed.canViewFullProfile));
      } catch (error) {
        setRelationshipStatus(previousStatus);
        setFriendshipId(previousFriendshipId);
        setMessage(error instanceof Error && error.message === "auth" ? "Belépés szükséges" : "Nem sikerült menteni.");
      }
    });
  }

  const trigger = children ?? (
    <span className="inline-flex items-center gap-1.5">
      <UserAvatar user={user} size="sm" />
      <span>{name}</span>
    </span>
  );

  if (deleted) {
    return (
      <span className="inline-flex min-w-0 items-center gap-1.5 align-middle text-[#273E47]/65">
        {trigger}
      </span>
    );
  }

  return (
    <span ref={rootRef} className="relative inline-flex align-middle">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        className="inline-flex min-w-0 items-center gap-1.5 rounded-full text-left transition hover:text-[#A4243B] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#A4243B]/20"
      >
        {trigger}
      </button>

      {open ? (
        <span className="absolute left-0 top-full z-[90] mt-2 block w-[min(18rem,calc(100vw-2rem))] rounded-[18px] border border-[#D8C99B]/70 bg-[#FCFBF8] p-4 text-left shadow-xl">
          <span className="flex items-start gap-3">
            <UserAvatar user={user} size="lg" />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-base font-black text-[#273E47]">{name}</span>
              <span className="block truncate text-xs font-bold text-[#273E47]/60">@{user.username}</span>
              <span className="mt-2 flex flex-wrap items-center gap-1.5">
                {canViewFullProfile ? <FavoriteTeamMark team={user.favoriteTeam ?? null} /> : null}
                {canViewFullProfile && (user.role === "ADMIN" || user.role === "EDITOR") ? (
                  <span className="rounded-full bg-[#273E47] px-2 py-0.5 text-[10px] font-black uppercase text-white">
                    {user.role === "ADMIN" ? "Admin" : "Editor"}
                  </span>
                ) : null}
                {canViewFullProfile && user.forumModerator ? (
                  <span className="rounded-full bg-[#A4243B] px-2 py-0.5 text-[10px] font-black uppercase text-white">
                    Moderator
                  </span>
                ) : null}
                {canViewFullProfile && user.forumTrusted ? (
                  <span className="rounded-full bg-[#D8973C]/15 px-2 py-0.5 text-[10px] font-black uppercase text-[#D8973C]">
                    Trusted
                  </span>
                ) : null}
              </span>
            </span>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Popover bezárása"
              className="grid h-7 w-7 shrink-0 place-items-center rounded-full text-[#273E47]/60 transition hover:bg-[#EEF0EA] hover:text-[#273E47]"
            >
              <X size={14} />
            </button>
          </span>

          <span className="mt-4 grid gap-2">
            {!canViewFullProfile ? (
              <span className="rounded-[14px] bg-[#EEF0EA] px-3 py-2 text-xs font-bold leading-5 text-[#273E47]/75">
                A teljes profil megtekintéséhez barátnak kell lennetek.
              </span>
            ) : null}
            {canViewFullProfile ? (
              <Link
                href={`/u/${user.username}`}
                className="inline-flex h-9 items-center justify-center rounded-full bg-[#273E47] px-3 text-xs font-black uppercase text-white transition hover:bg-[#A4243B]"
              >
                Profil megnyitása
              </Link>
            ) : null}
            {!currentUserId ? (
              <span className="rounded-full bg-[#EEF0EA] px-3 py-2 text-center text-xs font-black uppercase text-[#273E47]/70">
                Belépés szükséges
              </span>
            ) : isSelf ? null : relationshipStatus === "NONE" || relationshipStatus === "DECLINED" ? (
              <button
                type="button"
                disabled={pending}
                onClick={() => runAction("/api/friends/request", { addresseeId: user.id }, "OUTGOING_PENDING")}
                className="inline-flex h-9 items-center justify-center gap-1.5 rounded-full border border-[#D8C99B]/70 bg-white px-3 text-xs font-black uppercase text-[#273E47] transition hover:border-[#A4243B] disabled:opacity-60"
              >
                <UserPlus size={14} /> Barát meghívó küldése
              </button>
            ) : relationshipStatus === "INCOMING_PENDING" && friendshipId ? (
              <span className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  disabled={pending}
                  onClick={() => runAction("/api/friends/respond", { friendshipId, response: "accept" }, "FRIEND")}
                  className="h-9 rounded-full bg-[#273E47] px-3 text-xs font-black uppercase text-white transition hover:bg-[#A4243B] disabled:opacity-60"
                >
                  Elfogadás
                </button>
                <button
                  type="button"
                  disabled={pending}
                  onClick={() => runAction("/api/friends/respond", { friendshipId, response: "decline" }, "DECLINED")}
                  className="h-9 rounded-full border border-[#D8C99B]/70 bg-white px-3 text-xs font-black uppercase text-[#273E47] transition hover:border-[#A4243B] disabled:opacity-60"
                >
                  Elutasítás
                </button>
              </span>
            ) : relationshipStatus === "OUTGOING_PENDING" && friendshipId ? (
              <span className="grid gap-2">
                <span className="rounded-full bg-[#EEF0EA] px-3 py-2 text-center text-xs font-black uppercase text-[#273E47]/70">
                  Meghívó elküldve
                </span>
                <button
                  type="button"
                  disabled={pending}
                  onClick={() => runAction("/api/friends/remove", { friendshipId, mode: "cancel" }, "NONE")}
                  className="h-9 rounded-full border border-[#D8C99B]/70 bg-white px-3 text-xs font-black uppercase text-[#273E47] transition hover:border-[#A4243B] disabled:opacity-60"
                >
                  Visszavonás
                </button>
              </span>
            ) : relationshipStatus === "FRIEND" && friendshipId ? (
              <button
                type="button"
                disabled={pending}
                onClick={() => runAction("/api/friends/remove", { friendshipId, mode: "remove" }, "NONE")}
                className="inline-flex h-9 items-center justify-center gap-1.5 rounded-full bg-[#A4243B] px-3 text-xs font-black uppercase text-white transition hover:bg-[#8f1f33] disabled:opacity-60"
              >
                <UserRoundCheck size={14} /> Barát törlése
              </button>
            ) : null}
            {message ? <span className="text-center text-xs font-bold text-[#273E47]/65">{message}</span> : null}
          </span>
        </span>
      ) : null}
    </span>
  );
}
